// src/config/nodeConfig.js
import React from 'react';
import { NODE_COLORS } from './theme';

// Icons
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import InputOutlinedIcon from '@mui/icons-material/InputOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import OutputOutlinedIcon from '@mui/icons-material/OutputOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';

export const NODE_TYPES = {
  INPUT: 'input_node',
  PHOTO: 'photo_node',
  PDF: 'pdf_node',
  STATE_HISTORY: 'state_history_node',
  CODE: 'code_node',
  AI: 'ai_node',
  TEMPLATE: 'template_node',
  OUTPUT: 'output_node',
};

export const OUTPUT_FORMATS = {
  MARKDOWN_TO_PDF: 'markdown_to_pdf',
  DOCX_TO_PDF: 'docx_to_pdf',
  DOCX: 'docx',
  CSV: 'csv',
  XLSX: 'xlsx'
};

export const OUTPUT_FORMAT_CONFIG = {
  [OUTPUT_FORMATS.MARKDOWN_TO_PDF]: {
    label: 'Markdown to PDF',
    description: 'Convert markdown content to PDF document',
    icon: (props) => <OutputOutlinedIcon {...props} />,
    color: '#6366f1'
  },
  [OUTPUT_FORMATS.DOCX_TO_PDF]: {
    label: 'DOCX to PDF',
    description: 'Generate PDF using DOCX template',
    icon: (props) => <ArticleOutlinedIcon {...props} />,
    color: '#dc2626',
    requiresTemplate: true
  },
  [OUTPUT_FORMATS.DOCX]: {
    label: 'DOCX Document',
    description: 'Generate Word document using DOCX template',
    icon: (props) => <ArticleOutlinedIcon {...props} />,
    color: '#2563eb',
    requiresTemplate: true
  },
  [OUTPUT_FORMATS.CSV]: {
    label: 'CSV Export',
    description: 'Export data as CSV file for spreadsheets',
    icon: (props) => <TableChartOutlinedIcon {...props} />,
    color: '#059669'
  },
  [OUTPUT_FORMATS.XLSX]: {
    label: 'Excel Document',
    description: 'Generate Excel spreadsheet using XLSX template with {{variables}}',
    icon: (props) => <TableChartOutlinedIcon {...props} />,
    color: '#16a34a',
    requiresTemplate: true
  }
};

export const NODE_CONFIG = {
  [NODE_TYPES.INPUT]: {
    type: 'input',
    label: 'Input',
    colors: NODE_COLORS.INPUT,
    icon: (props) => <InputOutlinedIcon {...props} />,
    description: 'The starting point of your workflow, providing transcript data to subsequent nodes.',
    hasSourceHandle: true,
    hasTargetHandle: false,
    defaultData: {}
  },

  [NODE_TYPES.PHOTO]: {
    type: 'photo',
    label: 'Photo',
    colors: NODE_COLORS.PHOTO,
    icon: (props) => <PhotoCameraOutlinedIcon {...props} />,
    description: 'Provides uploaded photo data to subsequent nodes. Connect directly to an AI node to send the images to the model for vision analysis.',
    hasSourceHandle: true,
    hasTargetHandle: false,
    defaultData: {}
  },
  
  [NODE_TYPES.PDF]: {
    type: 'pdf',
    label: 'PDF',
    colors: NODE_COLORS.PDF,
    icon: (props) => <PictureAsPdfOutlinedIcon {...props} />,
    description: 'Provides the text and tables extracted from uploaded PDF files to subsequent nodes. Tables are passed as Markdown.',
    hasSourceHandle: true,
    hasTargetHandle: false,
    defaultData: {}
  },

  [NODE_TYPES.STATE_HISTORY]: {
    type: 'state_history',
    label: 'State History',
    colors: NODE_COLORS.STATE_HISTORY,
    icon: (props) => <HistoryOutlinedIcon {...props} />,
    description: 'Provides the full history of state operations from all previous job runs. Only available for stateful projects.',
    hasSourceHandle: true,
    hasTargetHandle: true,
    defaultData: {}
  },

  [NODE_TYPES.CODE]: {
    type: 'code',
    label: 'Code',
    colors: NODE_COLORS.CODE,
    icon: (props) => <CodeOutlinedIcon {...props} />,
    description: 'Executes custom code to transform and process data within your workflow.',
    hasSourceHandle: true,
    hasTargetHandle: true,
    defaultData: {
      code: ''
    }
  },
  
  [NODE_TYPES.AI]: {
    type: 'ai',
    label: 'AI',
    colors: NODE_COLORS.AI,
    icon: (props) => <SmartToyOutlinedIcon {...props} />,
    description: 'Processes data using AI models and generates outputs based on prompts. When a Photo node is connected, the images are sent to the model (vision).',
    hasSourceHandle: true,
    hasTargetHandle: true,
    defaultData: {
      prompt: '',
      examples: []
    }
  },
  
  [NODE_TYPES.TEMPLATE]: {
    type: 'template',
    label: 'Template',
    colors: NODE_COLORS.TEMPLATE,
    icon: (props) => <DescriptionOutlinedIcon {...props} />,
    description: 'Creates structured output using variables from previous nodes.',
    hasSourceHandle: true,
    hasTargetHandle: true,
    defaultData: {
      template: ''
    }
  },
  
  [NODE_TYPES.OUTPUT]: {
    type: 'output',
    label: 'Output',
    colors: NODE_COLORS.OUTPUT,
    icon: (props) => <OutputOutlinedIcon {...props} />,
    description: 'The final output destination of your workflow.',
    hasSourceHandle: false,
    hasTargetHandle: true,
    defaultData: {
      output_format: OUTPUT_FORMATS.MARKDOWN_TO_PDF
    }
  },
};